/**
 * Web Push subscription helpers. Thin interface so the Capacitor wrap later
 * can swap to APNs/FCM by replacing this module — callers do not change.
 */
import { supabase } from "@/integrations/supabase/client";
import { isPWA, isIOS } from "@/lib/platform";

// Public VAPID key (safe to embed). Server signs with the matching private key.
export const VAPID_PUBLIC_KEY =
  "BPfockjA6Kb8t8-cF_7ON5IoX2548T09sTT0WKS2s40Y8joLaJTyxfujUtKlZ46yXWhbfk-flp1pv9dyNZ_JJt4";

const SW_URL = "/push-sw.js";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function bufToB64Url(buf: ArrayBuffer | null) {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function pushSupported(): boolean {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!("PushManager" in window)) return false;
  if (!("Notification" in window)) return false;
  // iOS requires installed PWA
  if (isIOS() && !isPWA()) return false;
  return true;
}

export function currentPermission(): NotificationPermission {
  if (typeof Notification === "undefined") return "denied";
  return Notification.permission;
}

async function registerWorker(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_URL, { scope: "/" });
}

export async function subscribePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm };

  const reg = await registerWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const json = sub.toJSON();
  const { data: userRes } = await supabase.auth.getUser();
  if (!userRes.user) return { ok: false, reason: "not-authenticated" };

  await supabase.from("push_subscriptions").upsert(
    {
      user_id: userRes.user.id,
      endpoint: sub.endpoint,
      p256dh: json.keys?.p256dh ?? bufToB64Url(sub.getKey("p256dh")),
      auth: json.keys?.auth ?? bufToB64Url(sub.getKey("auth")),
      platform: "web",
      user_agent: navigator.userAgent.slice(0, 255),
      last_seen_at: new Date().toISOString(),
      failed_at: null,
      failure_count: 0,
    },
    { onConflict: "user_id,endpoint" },
  );
  return { ok: true };
}

export async function unsubscribePush(): Promise<void> {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await supabase.from("push_subscriptions").delete().eq("endpoint", sub.endpoint);
    await sub.unsubscribe();
  }
}

export async function sendTestPush(): Promise<{ ok: boolean; error?: string }> {
  const { data, error } = await supabase.functions.invoke("send-push", {
    body: { test: true },
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, ...data };
}
